
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {PushNotificationEventType} from './PushNotificationEventType';
import {FieldModelPushNotificationEventType} from './PushNotificationEventType';
import {NotificationTemplate} from './NotificationTemplate';
import {FieldModelNotificationTemplate} from './NotificationTemplate';

/**
 * PushNotificationTemplate struct
 */
class PushNotificationTemplate {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!number=} sortOrder
   * @param {!string=} name
   * @param {!PushNotificationEventType=} eventType
   * @param {!Map=} templatesByLanguage
   * @param {!boolean=} isDisabled
   * @param {!number=} throttleTimeInSeconds
   * @constructor
   */
  constructor (id = UUID.sequential(), sortOrder = 0, name = '', eventType = new PushNotificationEventType(), templatesByLanguage = new Map(), isDisabled = false, throttleTimeInSeconds = 0) {
    this.id = id
    this.SortOrder = sortOrder
    this.Name = name
    this.EventType = eventType
    this.TemplatesByLanguage = templatesByLanguage
    this.IsDisabled = isDisabled
    this.ThrottleTimeInSeconds = throttleTimeInSeconds
  }

  /**
   * Copy struct (shallow copy)
   * @this {!PushNotificationTemplate}
   * @param {!PushNotificationTemplate} other Other struct
   * @returns {!PushNotificationTemplate} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.SortOrder != null) {
      this.SortOrder = other.SortOrder
    } else {
      this.SortOrder = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.EventType != null) {
      this.EventType = PushNotificationEventType.fromObject(other.EventType)
    } else {
      this.EventType = null
    }
    if (other.TemplatesByLanguage != null) {
      this.TemplatesByLanguage = new Map()
      Object.keys(other.TemplatesByLanguage).forEach(key => {
        if (key != null) {
          let tempKey
          tempKey = new UUID(key)
          if (other.TemplatesByLanguage[key] != null) {
            let tempValue
            tempValue = NotificationTemplate.fromObject(other.TemplatesByLanguage[key])
            this.TemplatesByLanguage.set(tempKey, tempValue)
          } else {
            this.TemplatesByLanguage.set(tempKey, null)
          }
        }
      })
    } else {
      this.TemplatesByLanguage = null
    }
    if (other.IsDisabled != null) {
      this.IsDisabled = other.IsDisabled
    } else {
      this.IsDisabled = null
    }
    if (other.ThrottleTimeInSeconds != null) {
      this.ThrottleTimeInSeconds = other.ThrottleTimeInSeconds
    } else {
      this.ThrottleTimeInSeconds = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!PushNotificationTemplate}
   * @returns {!PushNotificationTemplate} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new PushNotificationTemplateModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new PushNotificationTemplateModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!PushNotificationTemplate}
   * @param {!PushNotificationTemplate} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof PushNotificationTemplate)) {
      throw new TypeError('Instance of PushNotificationTemplate is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!PushNotificationTemplate}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      SortOrder: ((this.SortOrder != null) ? this.SortOrder : null),
      Name: ((this.Name != null) ? this.Name : null),
      EventType: ((this.EventType != null) ? this.EventType : null),
      TemplatesByLanguage: ((this.TemplatesByLanguage != null) ? fbe.MapToObject(new Map(Array.from(this.TemplatesByLanguage, item => [((item[0] != null) ? item[0].toString() : null), ((item[1] != null) ? item[1] : null)]))) : null),
      IsDisabled: ((this.IsDisabled != null) ? this.IsDisabled : null),
      ThrottleTimeInSeconds: ((this.ThrottleTimeInSeconds != null) ? this.ThrottleTimeInSeconds : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return PushNotificationTemplate.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!PushNotificationTemplate} other Object value
   * @returns {!PushNotificationTemplate} Created struct
   */
  static fromObject (other) {
    return new PushNotificationTemplate().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!PushNotificationTemplate}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return PushNotificationTemplate.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!PushNotificationTemplate}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 213
  }
}

export { PushNotificationTemplate };

/**
 * Fast Binary Encoding PushNotificationTemplate field model
 */
class FieldModelPushNotificationTemplate extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.SortOrder = new fbe.FieldModelUInt32(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Name = new fbe.FieldModelString(buffer, this.SortOrder.fbeOffset + this.SortOrder.fbeSize)
    this.EventType = new FieldModelPushNotificationEventType(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.TemplatesByLanguage = new fbe.FieldModelMap(new fbe.FieldModelUUID(buffer, this.EventType.fbeOffset + this.EventType.fbeSize), new FieldModelNotificationTemplate(buffer, this.EventType.fbeOffset + this.EventType.fbeSize), buffer, this.EventType.fbeOffset + this.EventType.fbeSize)
    this.IsDisabled = new fbe.FieldModelBool(buffer, this.TemplatesByLanguage.fbeOffset + this.TemplatesByLanguage.fbeSize)
    this.ThrottleTimeInSeconds = new fbe.FieldModelUInt32(buffer, this.IsDisabled.fbeOffset + this.IsDisabled.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.SortOrder.fbeSize + this.Name.fbeSize + this.EventType.fbeSize + this.TemplatesByLanguage.fbeSize + this.IsDisabled.fbeSize + this.ThrottleTimeInSeconds.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.SortOrder.fbeExtra + this.Name.fbeExtra + this.EventType.fbeExtra + this.TemplatesByLanguage.fbeExtra + this.IsDisabled.fbeExtra + this.ThrottleTimeInSeconds.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelPushNotificationTemplate.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 213
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SortOrder.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.EventType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.EventType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EventType.fbeSize

    if ((fbeCurrentSize + this.TemplatesByLanguage.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TemplatesByLanguage.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TemplatesByLanguage.fbeSize

    if ((fbeCurrentSize + this.IsDisabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsDisabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsDisabled.fbeSize

    if ((fbeCurrentSize + this.ThrottleTimeInSeconds.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ThrottleTimeInSeconds.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ThrottleTimeInSeconds.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!PushNotificationTemplate} fbeValue Default value, defaults is new PushNotificationTemplate()
   * @returns {!PushNotificationTemplate} PushNotificationTemplate value
   */
  get (fbeValue = new PushNotificationTemplate()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!PushNotificationTemplate} fbeValue PushNotificationTemplate value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.SortOrder.fbeSize) <= fbeStructSize) {
      fbeValue.SortOrder = this.SortOrder.get()
    } else {
      fbeValue.SortOrder = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SortOrder.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.EventType.fbeSize) <= fbeStructSize) {
      fbeValue.EventType = this.EventType.get()
    } else {
      fbeValue.EventType = new PushNotificationEventType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.EventType.fbeSize

    if ((fbeCurrentSize + this.TemplatesByLanguage.fbeSize) <= fbeStructSize) {
      this.TemplatesByLanguage.get(fbeValue.TemplatesByLanguage)
    } else {
      fbeValue.TemplatesByLanguage.clear()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TemplatesByLanguage.fbeSize

    if ((fbeCurrentSize + this.IsDisabled.fbeSize) <= fbeStructSize) {
      fbeValue.IsDisabled = this.IsDisabled.get()
    } else {
      fbeValue.IsDisabled = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsDisabled.fbeSize

    if ((fbeCurrentSize + this.ThrottleTimeInSeconds.fbeSize) <= fbeStructSize) {
      fbeValue.ThrottleTimeInSeconds = this.ThrottleTimeInSeconds.get(0)
    } else {
      fbeValue.ThrottleTimeInSeconds = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ThrottleTimeInSeconds.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelPushNotificationTemplate}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelPushNotificationTemplate}
   * @param {!PushNotificationTemplate} fbeValue PushNotificationTemplate value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelPushNotificationTemplate}
   * @param {PushNotificationTemplate} fbeValue PushNotificationTemplate value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.SortOrder.set(fbeValue.SortOrder)
    this.Name.set(fbeValue.Name)
    this.EventType.set(fbeValue.EventType)
    this.TemplatesByLanguage.set(fbeValue.TemplatesByLanguage)
    this.IsDisabled.set(fbeValue.IsDisabled)
    this.ThrottleTimeInSeconds.set(fbeValue.ThrottleTimeInSeconds)
  }
}

export { FieldModelPushNotificationTemplate };

/**
 * Fast Binary Encoding PushNotificationTemplate model
 */
class PushNotificationTemplateModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelPushNotificationTemplate(this.buffer, 4)
  }

  /**
   * Get the PushNotificationTemplate model
   * @this {!PushNotificationTemplateModel}
   * @returns {!FieldModelPushNotificationTemplate} model PushNotificationTemplate model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!PushNotificationTemplateModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!PushNotificationTemplateModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return PushNotificationTemplateModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!PushNotificationTemplateModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelPushNotificationTemplate.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!PushNotificationTemplateModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!PushNotificationTemplateModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!PushNotificationTemplateModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!PushNotificationTemplateModel}
   * @param {!PushNotificationTemplate} value PushNotificationTemplate value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!PushNotificationTemplateModel}
   * @param {!PushNotificationTemplate} value PushNotificationTemplate value, defaults is new PushNotificationTemplate()
   * @return {!object} Deserialized PushNotificationTemplate value and its size
   */
  deserialize (value = new PushNotificationTemplate()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new PushNotificationTemplate(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new PushNotificationTemplate(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!PushNotificationTemplateModel}
   * @param {!number} prev Previous PushNotificationTemplate model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { PushNotificationTemplateModel };
